import { getGroupbyLabel, isTimeGroupbyField } from './chartUtils'
import { isGroupbyAvailable } from './collectionGroupby'

export const AUTO_INTERVAL = 'auto'

export const CHART_INTERVALS = [
  { value: AUTO_INTERVAL, label: 'Auto' },
  { value: 'hour', label: 'Hourly' },
  { value: 'day', label: 'Daily' },
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
]

const AUTO_INTERVAL_THRESHOLDS = [
  { maxDays: 2, interval: 'hour' },
  { maxDays: 92, interval: 'day' },
  { maxDays: 730, interval: 'month' },
]

const FALLBACK_INTERVAL = 'year'

export const QUANTIFIABLE_INTERVALS = ['hour', 'day']

export const MAX_INTERVAL_QUANTITY = 99

const INTERVAL_FORMATS = {
  hour: 'MM/dd HH:mm',
  day: 'MM/dd/yyyy',
  month: 'MM/yyyy',
  year: 'yyyy',
}

export function supportsInterval(field) {
  if (!isTimeGroupbyField(field)) return false

  return !String(field).endsWith('_unittime')
}

export function resolveAutoInterval(startDate, endDate) {
  if (!startDate || !endDate) return 'day'

  const days = Math.abs(endDate.diff(startDate, 'days')) + 1
  const match = AUTO_INTERVAL_THRESHOLDS.find(threshold => days <= threshold.maxDays)

  return match ? match.interval : FALLBACK_INTERVAL
}

export function applyIntervalToField(field, interval) {
  const suffix = String(field).endsWith('_session') ? '_session' : ''

  return `${interval}${suffix}`
}

export function resolveChartInterval(chart, dates) {
  const [field] = chart?.dimensions || []
  if (!supportsInterval(field)) return null

  const interval = chart?.interval || AUTO_INTERVAL
  if (interval !== AUTO_INTERVAL) return interval

  return resolveAutoInterval(dates?.startDate, dates?.endDate)
}

export function resolveChartDimensionField(chart, config) {
  const [field] = chart?.dimensions || []
  if (!field) return null

  const interval = resolveChartInterval(chart, config)
  if (!interval) return field

  const next = applyIntervalToField(field, interval)

  return isGroupbyAvailable(config?.collection, next) ? next : field
}

export function acceptsIntervalQuantity(interval) {
  return QUANTIFIABLE_INTERVALS.includes(interval)
}

export function normalizeIntervalQuantity(quantity) {
  const parsed = Math.trunc(Number(quantity))
  if (!Number.isFinite(parsed) || parsed < 1) return 1

  return Math.min(parsed, MAX_INTERVAL_QUANTITY)
}

export function resolveChartIntervalQuantity(chart, dates) {
  const interval = resolveChartInterval(chart, dates)
  if (!acceptsIntervalQuantity(interval)) return 1

  return normalizeIntervalQuantity(chart?.intervalQuantity)
}

export function resolveChartGroupbyField(chart, config) {
  const field = resolveChartDimensionField(chart, config)
  if (!field) return null

  const quantity = resolveChartIntervalQuantity(chart, config)
  if (quantity < 2) return field

  const format = INTERVAL_FORMATS[resolveChartInterval(chart, config)]
  if (!format) return field

  return `${field}:${quantity} ${encodeURIComponent(format)}`
}

export function getChartGroupingLabel(chart, config) {
  const field = resolveChartDimensionField(chart, config)
  if (!field) return ''

  const label = getGroupbyLabel(config?.collection, field).toLowerCase()
  const quantity = resolveChartIntervalQuantity(chart, config)

  return quantity < 2 ? label : `${quantity} ${label}s`
}
